import { ReactElement } from 'react';
import { Input, Modal, PrimaryButton, SnackbarStatus, Typography, useSnackbar } from '@weareopenr/component-library';
import ModalImg from 'src/assets/images/create-menu-item-modal.png';
import { useForm } from 'react-hook-form';
import styles from './VenueContactModal.module.scss';
import { FormAutocomplete } from '../FormElements';
import { ICountry } from 'src/types/venue.types';
import { IContactFormValues, IVenueModalContacts } from './types';
import { useUpdateVenueContact } from '../../hooks/useUpdateVenueContact/useUpdateVenueContact';
import { useCountries } from 'src/hooks/useCountries/useCountries';

const ModalContent = ({ closeModal, venue, addressId }: Omit<IVenueModalContacts, 'open'>): ReactElement => {
  const { data: countries } = useCountries();
  const { showSnackbar } = useSnackbar();
  const createNotificationAndClose = () => {
    showSnackbar({ status: SnackbarStatus.SUCCESS, message: 'Successfully updated venue contact information' });
    closeModal();
  };
  const { mutate, isLoading } = useUpdateVenueContact(venue.venueId, createNotificationAndClose, addressId);

  const address = venue.addresses?.[0];
  const {
    handleSubmit,
    register,
    control,
    formState: { dirtyFields, isSubmitting },
  } = useForm<IContactFormValues>({
    defaultValues: {
      addressLine1: address?.addressLine1 || '',
      postcode: address?.postcode || '',
      town: address?.town || '',
      countryCode: address?.country.alpha3code || null,
      email: address?.email || '',
      phoneNumber: address?.phoneNumber || '',
    },
    mode: 'onChange',
  });

  const onSubmit = (data: IContactFormValues) => {
    if (Object.keys(dirtyFields).length > 0) {
      mutate(data);
    } else {
      createNotificationAndClose();
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Typography size="xxlarge" weight="semi-bold" className={styles['heading-text']}>
        {`Edit contact for ${venue.name}`}
      </Typography>
      <div className={styles['content']}>
        <Input
          type="text"
          label="Address"
          register={register(`addressLine1`, {
            deps: ['countryCode'],
          })}
        />
        <Input
          type="text"
          label="Postcode"
          register={register('postcode', {
            deps: ['countryCode'],
          })}
        />
        <Input
          type="text"
          label="Town"
          register={register('town', {
            deps: ['countryCode'],
          })}
        />
        <FormAutocomplete
          name={'countryCode'}
          label="Country"
          key={`countryCode-${venue.venueId}`}
          control={control}
          options={countries?.map((country: ICountry) => ({
            id: country.alpha3code,
            name: country.name,
          }))}
          rules={{
            validate: (value, formValues) => {
              return (
                !!value ||
                (!formValues.addressLine1 &&
                  !formValues.postcode &&
                  !formValues.town &&
                  !formValues.email &&
                  !formValues.phoneNumber) ||
                'Country is required if at least one of the contact fields is provided'
              );
            },
          }}
        />
        <Input type="text" label="Email" register={register('email', { deps: ['countryCode'] })} />
        <Input type="text" label="Phone" register={register('phoneNumber', { deps: ['countryCode'] })} />
      </div>
      <PrimaryButton size="full-width" type="submit" loading={isLoading || isSubmitting}>
        Save
      </PrimaryButton>
    </form>
  );
};

const VenueContactModal = ({ closeModal, open, venue, addressId }: IVenueModalContacts): ReactElement => {
  return (
    <Modal closeModal={closeModal} open={open} image={ModalImg} imageAlt="Picture of Sides">
      <ModalContent closeModal={closeModal} venue={venue} addressId={addressId} />
    </Modal>
  );
};

export default VenueContactModal;
